<template>
  <b-modal
    ref="modal"
    :visible="true"
    static
    lazy
    centered
    scrollable
    no-fade
    title="Завершение обслуживания"
    hide-footer
    modal-class="cmp-modal"
    @hide="onHide"
    @hidden="onHidden"
  >
    <div class="cmp__task">
      <div class="cmp__id">{{ card.label }}</div>
      <div class="cmp__object">{{ card.objectName }}</div>
      <div v-if="card.address" class="cmp__address">{{ card.address }}</div>
    </div>
    <form @submit.prevent="submit" novalidate>
      <div class="cmp__section-title">Выполненные работы</div>
      <div v-if="worksLoading" class="cmp__loading">
        <span class="spinner-border spinner-border-sm text-primary" role="status"><span class="sr-only">Загрузка…</span></span>
      </div>
      <div v-else-if="works.status === 'error'" class="alert alert-danger py-2 px-3 d-flex align-items-center cmp__works-error" role="alert">
        <span>Не удалось загрузить работы: {{ errorMessage(works.error) }}</span>
        <button type="button" class="btn btn-sm btn-light ml-auto cmp__works-retry" @click="loadWorks">Повторить</button>
      </div>
      <div v-else-if="!rows.length" class="cmp__no-works">Работ нет</div>
      <div v-else class="cmp__works">
        <label v-for="row in rows" :key="row.id" class="cmp__work">
          <input
            type="checkbox"
            class="cmp__check"
            :checked="row.done"
            :disabled="busy || !!toggling[row.id]"
            @change="toggle(row, $event)"
          >
          <span class="cmp__work-title">{{ row.title }}</span>
          <span v-if="toggling[row.id]" class="spinner-border spinner-border-sm ml-auto" role="status" aria-hidden="true"></span>
        </label>
      </div>

      <div class="form-group mt-3">
        <label class="cmp__label" for="cmp-paper">Остаток бумаги после обслуживания <span class="text-danger">*</span></label>
        <input id="cmp-paper" v-model="form.paper_balance" type="number" inputmode="numeric" min="0" step="1" class="form-control cmp__paper" :disabled="busy">
      </div>
      <div class="form-group">
        <label class="cmp__label" for="cmp-comment">
          Комментарий инженера
          <span class="cmp__label-note" :class="{ 'cmp__label-note--required': undone }">· обязателен при частичном закрытии</span>
        </label>
        <textarea id="cmp-comment" v-model="form.completion_comment" rows="3" class="form-control cmp__comment" :maxlength="commentMax" :disabled="busy"></textarea>
      </div>

      <div v-if="undone" class="cmp__hint">Не все работы отмечены — задача закроется частично</div>
      <ul v-if="errors.length" class="alert alert-danger py-2 pl-4 mb-3 cmp__errors" role="alert">
        <li v-for="(e, i) in errors" :key="i">{{ e }}</li>
      </ul>
      <div v-if="submitError" class="alert alert-danger py-2 px-3 mb-3 cmp__submit-error" role="alert">{{ submitError }}</div>

      <div class="cmp__actions">
        <button type="button" class="btn btn-outline-secondary cmp__cancel" :disabled="busy" @click="hideModal">Отмена</button>
        <button type="submit" class="btn btn-success cmp__submit" :disabled="busy || !worksReady || toggleBusy">
          <span v-if="busy" class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>Выполнить задачу
        </button>
      </div>
    </form>
  </b-modal>
</template>

<script>
import * as api from '@/components/management/dispatcher/api';
import { createResource, loadResource, isReady, errorMessage } from '@/components/management/dispatcher/resource';
import { notifyError } from '@/components/management/dispatcher/notify';
import modalCloseMixin from '@/components/management/dispatcher/map/modalCloseMixin';
import { workRows, hasUndone } from '@/components/field/fieldTasksLogic';
import {
  COMMENT_MAX, emptyCompletionForm, validateCompletionForm, completionPayload,
} from '@/components/field/completionPayload';

/**
 * «Завершение обслуживания» (KOP-40 spec §6.4). Works are loaded fresh; every tick is saved at
 * once (`set-done`); «Выполнить задачу» posts change-status → done. Closing is blocked while busy.
 */
export default {
  name: 'CompleteTaskModal',
  mixins: [modalCloseMixin],
  props: {
    card: { type: Object, required: true },
  },
  data() {
    return {
      works: createResource(),
      rows: [],
      toggling: {},
      form: emptyCompletionForm(this.card.paper),
      errors: [],
      submitError: null,
      busy: false,
    };
  },
  computed: {
    commentMax() { return COMMENT_MAX; },
    worksReady() { return isReady(this.works); },
    toggleBusy() { return Object.keys(this.toggling).length > 0; },
    worksLoading() { return this.works.status === 'loading' || this.works.status === 'idle'; },
    undone() { return this.worksReady && hasUndone(this.rows); },
  },
  created() {
    this.loadWorks();
  },
  methods: {
    errorMessage,
    async loadWorks() {
      const ok = await loadResource(this.works, () => api.getFieldWorksByTask(this.card.id));
      this.rows = ok ? workRows(this.works.data) : [];
    },
    /**
     * Saves one tick at once. `works-changed` is emitted before the request so the parent still reloads if
     * the modal is closed while it is in flight; the box always follows the model when the request settles.
     */
    async toggle(row, event) {
      if (this.busy || this.toggling[row.id]) return;
      const checked = !!(event && event.target && event.target.checked);
      this.$set(this.toggling, row.id, true);
      this.$emit('works-changed');
      try {
        const saved = await api.setFieldWorkDone({ id: row.id, is_done: checked });
        row.done = saved && typeof saved.is_done === 'boolean' ? saved.is_done : checked;
      } catch (e) {
        notifyError(this, e, 'Не удалось сохранить отметку');
      } finally {
        if (event && event.target) event.target.checked = row.done;
        this.$delete(this.toggling, row.id);
      }
    },
    async submit() {
      if (this.busy || !this.worksReady || this.toggleBusy) return;
      this.submitError = null;
      this.errors = validateCompletionForm(this.form, this.rows);
      if (this.errors.length) return;
      this.busy = true;
      try {
        await api.completeTask(completionPayload(this.card.id, this.form));
        this.$emit('completed', this.card);
        this.hideModal();
      } catch (e) {
        this.submitError = errorMessage(e, 'Не удалось выполнить задачу');
        // The POST may have succeeded server-side while the response was lost, so the page reloads the list on close.
        this.$emit('works-changed');
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.cmp__task { margin-bottom: 12px; }
.cmp__id { font-family: SFMono-Regular, Menlo, monospace; font-size: 12px; color: #8b918c; }
.cmp__object { font-weight: 700; font-size: 15px; color: #1d221f; }
.cmp__address { font-size: 13px; color: #8b918c; }
.cmp__section-title { font-size: 11px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; color: #8b918c; margin-bottom: 6px; }
.cmp__loading { display: flex; justify-content: center; padding: 8px 0; }
.cmp__no-works { color: #8b918c; font-size: 13px; }
.cmp__works { display: flex; flex-direction: column; gap: 8px; }
.cmp__work { display: flex; align-items: center; gap: 10px; min-height: 44px; margin: 0; padding: 0 12px; border: 1px solid #e3e5e1; border-radius: 10px; font-size: 14px; color: #1d221f; cursor: pointer; }
.cmp__check { width: 18px; height: 18px; margin: 0; flex: none; }
.cmp__label { font-size: 11px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; color: #8b918c; }
.cmp__label-note { font-weight: 600; }
.cmp__label-note--required { color: #c0492b; }
.cmp__hint { background: #fdf3e1; color: #9a6a00; border-radius: 8px; padding: 8px 10px; font-size: 13px; margin-bottom: 12px; }
.cmp__actions { display: flex; gap: 8px; }
.cmp__actions .btn { flex: 1; min-height: 44px; font-weight: 700; }
@media (max-width: 575.98px) {
  ::v-deep .cmp-modal .modal-dialog { margin: 0; max-width: none; width: 100%; min-height: 100%; display: flex; align-items: stretch; }
  ::v-deep .cmp-modal .modal-content { flex: 1 1 auto; border-radius: 0; border: 0; }
}
</style>
